/// <reference path="../../../../WebUI/typings/tsd.d.ts" />
/// <reference path="../../Controllers/plugin.d.ts"/>

import * as React from "react";
import * as ReactDOM from "react-dom";
import { MessageBox } from './../../Dialogs/MessageBox';

import { PageStatusFlags } from "./ControllerBase";
import { BotControllerBase } from "./BotControllerBase";
import { DevPortalAppBase, IAnnouncements } from "../../DevPortalAppBase";
import { DevPortalApp } from "../DevPortalApp";
import * as Rest from "../../Generated/RestClient";
import * as Dialogs from "../../Dialogs/MessageBox";
import { ProgressDialog } from "../../Dialogs/ProgressDialog";
import { GenericDialog } from "../../Dialogs/GenericDialog";
import { NavLinks } from "../../../../Intercom.ReactUI/Views/index";
import { Resources } from "../../Resources/Intercom.Resources.DevPortal";
import { clientAppSettings } from "../../Models/ClientAppSettings";
import { BotUtils, BotTypes, BotUtilsV2, RollbackEndpointVersionName } from "../../Utils/BotUtils";
import { BotEditController } from "./BotEditController";
import {
    BotProgress, V3Update, EmptyBotPage,
    BotTabs, BotPage, isStillDeploying, isDeploymentFinished, BotPageProps, BotPageState,
    BotAppSettingsProps, BotAppSettings, BotAppCIProps,
    Route
} from "../Views/index";
import { WebChatPage, WebChatPageProps } from "../Views/WebChatPage";
import { BotCodeEditorController } from "./BotCodeEditorController";
import isInFrame from "../../../../Intercom.ReactUI/Utils/InFrame";
import { BotPageTab } from "../../Views/BotTabPage";
import * as EmbedCodeView from "../Views/EmbedCodePanel";
import { RuntimeConnectedShadeUtils } from "../../../../Intercom.ReactUI/Utils/RuntimeConnectedShadeUtils";
import { Environment } from "../../../../Intercom.ReactUI/Utils/Environment";
import { Poller, PollingModel } from "../../Models/PollingModel";
import { KuduClient, ArmClient, GraphApi, TenantInfo } from "../../Services/index";
import { downloadBotSource } from "../Services/downloadBotSrc";
import { parseApiError } from "../Services/parseApiError";
import { BotCodeSetupController } from "./BotCodeSetupController";
import getBotResourceId from "../Services/botResourceId";
import { getAzurePortalBladeUrl, BladeType } from "../Services/azurePortalLinks";
import openTab from "../Services/openTab";
import {relativeUrl} from "../../Utils/Links";
import {AzurePortalPageController} from "./AzurePortalPageController";
import { readCookie } from "../../../../Intercom.ReactUI/Utils/Cookies";
import {lazy} from "../../Decorators/index";
import { FlightUtils } from "../../Utils/FlightUtils";

// 15 seconds polling interval to check deployment status
export const deploymentPollingInterval = 15000;

// Must be kept in sync with enum BotTabs of Client/Views/v4/BotPage.tsx
const ServiceBotRoutes: Route[] = [
    {
        tab: BotTabs.Build,
        route: '/bots/build'
    },
    {
        tab: BotTabs.Channels,
        route: '/bots/channels'
    },
    {
        tab: BotTabs.Analytics,
        route: '/bots/analytics'
    },
    {
        tab: BotTabs.Settings,
        route: '/bots/settings'
    }
];

// Must be kept in sync with enum BotTabs of Client/Views/v4/BotPage.tsx (subset ok)
const SDKBotRoutes: Route[] = [
    {
        tab: BotTabs.Channels,
        route: '/bots/channels'
    },
    {
        tab: BotTabs.Analytics,
        route: '/bots/analytics'
    },
    {
        tab: BotTabs.Settings,
        route: '/bots/settings'
    }
];

const ABSv1Routes: Route[] = [{
    tab: BotTabs.Develop,
    route: '/bots/code'
}].concat(SDKBotRoutes);

const ABSCodeRoutes: Route[] = [{
    tab: BotTabs.Build,
    route: '/bots/codesetup'
}].concat(SDKBotRoutes);

export function Routes(botType: BotTypes): Route[] {
    switch (botType) {
        case BotTypes.ABSCode:
            return ABSCodeRoutes;
        case BotTypes.ABSv1:
            return isInFrame() ? ABSv1Routes : SDKBotRoutes;
        case BotTypes.ABSv2:
            return ServiceBotRoutes;
        case BotTypes.SDK:
            return SDKBotRoutes;
        default:
            return [];
    }
}

// set to true to debug app settings section without being hosted in azure portal
const debugAppSettings = false;

export class BotController extends BotControllerBase {
    private component: BotPage;
    private _botEditController: BotEditController;
    private _botCodeEditorController: BotCodeEditorController;
    private _deployPoller: Poller<boolean>;
    private botAppSettings: BotAppSettings;
    private botPageState: BotPageState;
    private _armClient: ArmClient;

    private set waitingForDeployment(val: boolean) {
        if (val) {
            this.statusBar = React.createElement(BotProgress, {});
        } else {
            this.statusBar = null;
        }
    }

    private get deployPoller(): Poller<boolean> {
        if (!this._deployPoller) {
            const pollSetting: PollingModel = {
                defaultInterval: deploymentPollingInterval,
                maxPendingPollRequests: 1
            };
            this._deployPoller = new Poller(pollSetting,
                () => this.pollDeploymentStatus(),
                deployed => {
                    if (deployed) {
                        this.waitingForDeployment = false;
                        this.deployPoller.stop();
                        if (this.component) {
                            this.component.setState(this.botPageState);
                        }
                    }
                });
        }
        return this._deployPoller;
    }

    @lazy()
    private get kuduClient(): JQueryPromise<KuduClient> {
        // In azure we already have the arm auth token - just use it directly.
        if (Environment.isInBotFrameworkAzureExtension) {
            // If we don't have a bot or access token yet just fall back to the old code - we should not need a kuduClient but to be safe leaving it for now.
            if (this.botSub && this.botSub.Bot && this.botSub.Bot.Endpoint && this.app.signedInUser && this.app.signedInUser.AzureToken) {
                const kuduUri = KuduClient.getKuduSiteName(this.botSub.Bot);
                return $.Deferred<KuduClient>().resolve(KuduClient.createFromArmToken(kuduUri, this.app.signedInUser.AzureToken));
            }
        }

        return Rest.CodeClient.getScmAuthz(this.botId)
            .then(authz => {
                return $.Deferred<KuduClient>().resolve(KuduClient.createFromScmAuthorization(authz));
            });
    }

    constructor(app: DevPortalAppBase, container?: BotControllerBase,
        private _botAppSettingsProps?: BotAppSettingsProps) {
        super(app);

        this.botPageState = {
            showWebChat: false,
            showWebChatPending: true,
            announcements: (this.app as DevPortalApp).announcements
        };

        this.containerController = container;

        window.refreshBot = () => {
            this.refreshFromRemote();
        };

        window.onbeforeunload = (event) => {
            if (this.component && this.component.state.authoringDirty) {
                const message = "You have unsaved changes in bot. Please click 'Save' button to save and train your bot. Otherwise you will lose all your changes. ";
                if (typeof event === 'undefined') {
                    event = window.event;
                }

                if (event) {
                    event.returnValue = message;
                }

                return message;
            }
        };

        RuntimeConnectedShadeUtils.on(RuntimeConnectedShadeUtils.AuthoringToolDirtyEventName, this.onDirtyMessage);
        RuntimeConnectedShadeUtils.on(RuntimeConnectedShadeUtils.AuthoringToolMaskEventName, this.onMaskMessage);
        RuntimeConnectedShadeUtils.on(RuntimeConnectedShadeUtils.AuthoringToolErrorEventName, this.onErrorMessage);
    }

    private get botAppSettingsProps(): BotAppSettingsProps {
        return this._botAppSettingsProps || (debugAppSettings ? {
            openBlade: this.openBlade.bind(this),
            handleChange: this.noop.bind(this),
            onSave: this.noop.bind(this),
            onDropIcon: this.noop.bind(this),
            downloadZip: this.downloadZip.bind(this),
            onZipDownloaded: this.noop.bind(this),
            initialBot: null,
            scmType: this.scmType,
            v4UI: true,
            kuduClient: this.kuduClient,
            armClient: this.armClient
        } : null);
    }

    private openBlade(bladeType: BladeType) {
        if (!isInFrame()) {
            this.getAzureBladeUrl(bladeType)
                .then(tabUrl => {
                    openTab(tabUrl);
                })
                .fail(() => {
                    const tabUrl = getAzurePortalBladeUrl(this.botSub.Bot, null, bladeType);
                    openTab(tabUrl);
                });
        } else if (this.containerController && this.containerController instanceof AzurePortalPageController) {
            this.containerController.openBlade(bladeType);
        }
    }

    private getAzureBladeUrl(bladeType: BladeType): JQueryPromise<string> {
        return this.graphClient.then(gc => {
            return gc.getTenantDetails(this.tenantId);
        })
        .then((tenantInfo: TenantInfo) => {
            const domainName = tenantInfo.DomainName;
            const tabUrl = getAzurePortalBladeUrl(this.botSub.Bot, domainName, bladeType);
            return $.Deferred<string>().resolve(tabUrl);
        });
    }

    private get botAppCIProps(): BotAppCIProps {
        return {
            openBlade: this.openBlade.bind(this),
            downloadZip: this.downloadZip.bind(this),
            bot: this.botSub ? this.botSub.Bot : null,
            scmType: () => this.scmType,
            bladeUrl: blade =>
                isInFrame() ? $.Deferred<string>().resolve('') : this.getAzureBladeUrl(blade),
            kuduClient: () => this.kuduClient,
            armClient: () => this.armClient,
            botRsrcId: $.Deferred<string>().resolve(this.botSub ? getBotResourceId(this.botSub.Bot) : null),
            botDeploymentLogFound: !!this.botSub,
            pendingDownload: false,
            blobUrl: null,
            onZipDownloaded: this.noop.bind(this),
            expandoOpenedAlways: true,
            hideTitle: true,
            toggleTip: null
        };
    }

    @lazy()
    private get armClient(): JQueryPromise<ArmClient> {
        return Rest.AccountClient.exchangeArmToken(this.tenantId).then(token => {
            return $.Deferred<ArmClient>().resolve(new ArmClient(token));
        });
    }

    private get tenantId(): string {
        return this.botSub && this.botSub.Bot && this.botSub.Bot.ProvisionInfo ?
            this.botSub.Bot.ProvisionInfo.TenantId : null;
    }

    @lazy()
    private get graphClient(): JQueryPromise<GraphApi> {
        // exchange our token for a graph token
        return Rest.AccountClient.exchangeGraphToken(this.tenantId).then(token => {
            return $.Deferred<GraphApi>().resolve(new GraphApi(token));
        });
    }

    private noop(): void {
        // no-op
    }

    private downloadZip(): JQueryPromise<Blob> {
        return downloadBotSource(this.kuduClient);
    }

    public unload() {
        if (this._deployPoller && !this._deployPoller.isStopped()) {
            this._deployPoller.stop();
        }
        super.unload();
    }

    public isUnloadable(callback: (unloadable: boolean) => void): void {
        if (this.hasUnsavedChanges()) {
            Dialogs.MessageBox.ask("Unsaved changes will be lost. Are you sure?").done(() => {
                callback(true);
            }).fail(() => {
                callback(false);
            });
        }
        else {
            callback(true);
        }
    }

    public hasUnsavedChanges(): boolean {
        return (this._botEditController && this._botEditController.hasUnsavedChanges()) ||
            (this._botCodeEditorController && this._botCodeEditorController.hasUnsavedChanges()) ||
            (this.component && this.component.state.authoringDirty);
    }

    public render(params: SimpleMvc.QueryParams): void {
        super.render(params);

        this.botId = params["id"];
        this.channel = params["channelId"];

        this.renderPage(this.botId);

        this.setActiveJumboActiveLink(NavLinks.mybots);
    }


    private handleTenantSwitching(xhr: JQueryXHR): boolean {
        const apiErr = parseApiError(xhr);
        if (apiErr && apiErr.code === 401 && apiErr.message.indexOf('/login?') >= 0) {
            window.location.href = apiErr.message;
            return true;
        }
        if (apiErr && apiErr.code === 'ExpiredAuthenticationToken') {
            // token expired, refresh
            const curUrl = relativeUrl(window.location);
            window.location.href = `/login?requestUrl=${encodeURIComponent(curUrl)}`;
            return true;
        }
        return false;
    }

    private checkBotDeployStatus(bot: Rest.BotService): void {
        if (Environment.isInBotFrameworkAzureExtension) {
            // In Azure Ibiza we get notification from the Blade when the bot is ready to receive messages. So just wait for that.
            return;
        }

        if (isStillDeploying(bot)) {
            this.waitingForDeployment = true;
            this.deployPoller.start();
        } else if (this.botPageState.showWebChatPending) {
            this.pollDeploymentStatus().then(ready => {
                if (!ready) {
                    this.deployPoller.start();
                }
            });
        }

        if (this.statusBar == null) {
            this.statusBar = React.createElement(V3Update, { bot: bot });
        }
    }

    private renderPage(botId: string): void {
        if (botId) {
            this.botId = botId;
            this.statusBar = React.createElement(BotProgress, { progress: Resources.loadingBot });
            this.setPageStatusFlag(PageStatusFlags.LoadingBot);

            Rest.BotManagerClient.getById(botId)
                .then(botSubscription => {
                    if (!this.isLoaded()) {
                        return;
                    }

                    this.botSub = botSubscription;

                    return this.loadBotState(botSubscription.Bot);
                    //this.checkBotDeployStatus(botSubscription.Bot);
                })
                .done(() => {
                    this.statusBar = null;
                    if (this.clearPageStatusFlag(PageStatusFlags.LoadingBot)) {
                        this.renderCommon();
                    }

                    this.saveBotStateAndRender(this.botSub);
                })
                .fail((jqXHR: JQueryXHR, textStatus, errorThrown) => {
                    this.statusBar = null;
                    if (this.clearPageStatusFlag(PageStatusFlags.LoadingBot)) {
                        this.renderCommon();
                    }

                    if (!this.handleTenantSwitching(jqXHR)) {
                        this.handleHttpResponseError(jqXHR);
                    }
                });
        }

        if (!this.botSub) {
            // bot not yet loaded, render the empty page
            ReactDOM.render(React.createElement(EmptyBotPage, {}), this.pageContent, () => {
                this.scrollToTop();
            });
        }
    }

    public loadBotState(bot: Rest.BotService): JQueryPromise<any[]> {
        const p1 = this.getBotLogs(bot.Id);
        return $.when(p1);
    }

    private get botEditorController(): BotEditController {
        if (!this._botEditController) {
            this._botEditController = new BotEditController(this.app, this, this.botAppSettingsProps);
        }
        return this._botEditController;
    }

    private get botCodeEditorController(): BotCodeEditorController {
        if (!this._botCodeEditorController) {
            this._botCodeEditorController = new BotCodeEditorController(this.app, this);
            if (this.botId) {
                this._botCodeEditorController.setBotId(this.botId,
                    this.botSub && this.botSub.Bot && this.botSub.Bot.ProvisionInfo ?
                        this.botSub.Bot.ProvisionInfo.SubscriptionId : null);
            }
        }
        return this._botCodeEditorController;
    }

    @lazy()
    private get botCodeSetupController(): BotCodeSetupController {
        return new BotCodeSetupController(this.app, this);
    }

    private get scmType(): JQueryPromise<string> {
        return this.kuduClient
            .then(client => client.settings)
            .then(settings => $.Deferred<string>().resolve(settings['ScmType']));
    }

    public getBotPageProps(botSub: Rest.BotSubscription): BotPageProps {
        let bot = botSub.Bot;
        let botType = BotUtils.getBotType(bot);
        let subscriptionKey = { primary: botSub.PrimarySubscriptionKey }
        this.channelDataHandlers.showEmbedHandler = (_lastFocus) => this.showEmbedCodes(_lastFocus);
        return {
            controller: this,
            routes: Routes,
            botSettingsTabProps: {
                bot: bot,
                subscriptionKeys: subscriptionKey,
                channelDataHandlers: this.channelDataHandlers,
                isAdmin: () => this.isAdminOrSupport(),
                isReviewer: () => this.isReviewer(),
                isSubmitForReviewFlighted: this.app.isSubmitForReviewFlighted(),
                isUserV3Flighted: this.app.isUserFeatureFlighted('v3'),
                configuredChannels: BotController.getConfiguredChannels(bot),
                reviewStatusDetail: botSub.ReviewStatusDetail,
            },
            botEditorProps: this.botEditorController.getBotEditorProps(bot,
                this.botAppSettingsProps,
                editor => { this.botEditorController.setComponent(editor); }),
            botCodeEditorProps: this.botCodeEditorController.getBotCodeEditorProps({
                scmType: () => {
                    if (this.containerController) {
                        return this.containerController['state'].scmType;
                    }
                    return 'None';
                },
                openTab: _.bind(this.openTab, this),
                onSetCodeEditor: editor => { this.botCodeEditorController.setComponent(editor); }
            }),
            botCodeSetupProps: {
                scmType: () => this.scmType,
                ciProps: this.botAppCIProps,
                setCodeSetup: x => {
                    this.botCodeSetupController.component = x;
                    if (this.botSub && this.botSub.Bot && x) {
                        x.setState({ bot: this.botSub.Bot });
                    }
                },
                setLoading: f => {
                    this.statusBar = f ? React.createElement(BotProgress, { progress: Resources.loadingBot}) : null;
                },
                onScmTypeChange: () => {
                    if (this.containerController) {
                        if (this.containerController instanceof AzurePortalPageController) {
                            this.containerController.refreshScmType();
                        }
                    }
                }
            },
            tabClickHandler: tab => this.onTabClick(tab),
            testClickHandler: () => this.onTestClick(),
            importBotHandler: (f) => this.onImportBot(f),
            channelConfigClickHandler: s => this.onChannelConfigNav(s),
            channelConfigInterface: {
                leaveChannelConfig: () => this.onChannelConfigLeave(),
                reloadBot: () => this.reloadBot(),
            },
            initialBot: bot,
            initialSelectedTab: this.tabFromPath(this.app.getCurrentPagePath(), botType),
            initialSubkeys: subscriptionKey,
            initialShowWebChat: this.botPageState.showWebChat,
            initialChannel: this.channel,
            initialAuthoringDirty: false,
            initialAuthoringError: false,
            onSetBotAppSettings: x => { this.setBotAppSettings(x) },
            azureToken: this.app.signedInUser.AzureToken,
            canWriteToBot: botSub.CanWriteToBot
        };
    }

    protected setBotAppSettings(x: any) {
        if (this.containerController) {
            super.setBotAppSettings(x);
        } else {
            this.botAppSettings = x;
            if (this.botAppSettings) {
                this.botAppSettings.setState(
                    {
                        bot: this.botSub ? this.botSub.Bot : null,
                        botDeploymentLogFound: true,
                    });
            }
        }
    }

    protected saveBotStateAndRender(botSub: Rest.BotSubscription) {
        let bot = botSub.Bot;
        let botType = BotUtils.getBotType(bot);
        let channelReviewStatuses = botSub.ReviewStatuses;

        this.botId = bot.Id;
        if (!this.botSub) {
            this.botSub = botSub;
            if (this.clearPageStatusFlag(PageStatusFlags.LoadingBot)) {
                this.renderCommon();
            }
        }

        this.channelDataHandlers.getChannelPublishStatus = (channelId: string) => {
            return channelReviewStatuses[channelId];
        };

        // if we finished the deployment without getting endpoint poll again
        if (!bot.Endpoint && bot.ProvisionInfo && isDeploymentFinished(bot)) {
            this.waitingForDeployment = true;
            this.deployPoller.start();
        }

        this.checkBotDeployStatus(bot);

        if (this.isWebChatRoute(this.app.getCurrentPagePath())) {
            // Webchat route used by Ibiza
            const element = React.createElement(WebChatPage, { bot: bot} as WebChatPageProps);
            ReactDOM.render<WebChatPageProps, WebChatPage>(element, this.pageContent);
        }
        else {
            // Standard bot page.
            const props: BotPageProps = this.getBotPageProps(botSub);
            const element = React.createElement(BotPage, props);

            this.updateTitle(bot.Name);

            this.component = ReactDOM.render<BotPageProps, BotPage>(element, this.pageContent, () => {
                this.scrollToTop();
                this.setupEventHandlers();
            }) as BotPage;
            // When pressing browser back to switch between tabs of this page React reuses existing component.
            // So we have to update the state of the existing component.
            this.component.setState({
                bot: bot,
                selectedTab: this.tabFromPath(this.app.getCurrentPagePath(), botType),
                channel: this.channel,
                reviewStatusDetail: botSub.ReviewStatusDetail
            });
            if (this.botCodeSetupController.component) {
                this.botCodeSetupController.component.setState({ bot: bot });
            }
            if (botType === BotTypes.ABSv1) {
                this.botCodeEditorController.setBotId(bot.Id, bot.ProvisionInfo ? bot.ProvisionInfo.SubscriptionId : null);
            }
        }
    }

    private onImportBot(file: any): JQueryPromise<any> {
        var requestVerificationToken = readCookie('Csrf-Token');
        var def = $.Deferred<any>();
        $.ajax({
            type: 'POST',
            url: `/api/botApp/${this.botId}/import`,
            contentType: file.type,
            headers: _.extend(Rest.ajaxHeaders || {}, { 'X-Csrf-Token': requestVerificationToken }),
            data: file,
            processData: false,
            success: r => def.resolve(r),
            error: e => def.reject(e)
        });

        this.showProgressMask = true;
        def.always(() => {
            this.showProgressMask = false;
        });

        return def;
    }

    private tabFromPath(path: string, botType: BotTypes): BotTabs {
        path = path ? path.toLowerCase().trim() : path;
        let tab;
        if (path !== '/bots') {
            tab = Routes(botType)
                .filter(entry => entry.route === path)
                .map(entry => entry.tab)
                .shift();
        }
        if (_.isUndefined(tab)) {
            if (!isInFrame()) {
                tab = botType == BotTypes.ABSv2 ? BotTabs.Build : BotTabs.Channels;
            } else {
                switch (botType) {
                    case BotTypes.ABSCode:
                    case BotTypes.ABSv2:
                        tab = BotTabs.Build;
                        break;
                    case BotTypes.ABSv1:
                        tab = BotTabs.Develop;
                        break;
                    default:
                        tab = BotTabs.Channels;
                        break;
                }
            }
        }
        return tab;
    }

    private openTab(tab: BotPageTab, callback?: Function) {
        if (tab == BotPageTab.settings) {
            this.onTabClick(BotTabs.Settings, callback)
        }
    }

    private onTabClick(tab: BotTabs, callback?: Function): void {
        this.component.setState({
            selectedTab: tab,
            channel: ""
        }, () => {
            if (callback) {
                callback();
            }
        });

        let botType = BotUtils.getBotType(this.component.state.bot);
        let route = Routes(botType)
            .filter(entry => entry.tab === tab)
            .map(entry => entry.route)
            .shift() || '/bots';
        if (this.containerController) {
            // the bot controller is hosted by another controller (should be AzurePortalPageController)
            // we don't want to have the router routes away and lose our inter-frame communication
            // with azure portal
        } else if (route === '/bots') {
            this.app.navigate(route, _.omit(this.params, "channelId"));
        } else {
            history.pushState(null, null, `${route}?id=${this.botId}`);
        }
    }

    private onTestClick() {
        this.botPageState.showWebChat = !this.botPageState.showWebChat;
        this.component.setState(this.botPageState);
    }

    private onChannelConfigNav(toChannel: string): void {
        const allowedChannels = BotUtilsV2.migrationOptionalChannels;
        // if this channel is already configured, not much point in giving the error. Shouldn't matter except for 'bingbizbot's
        const channelIsConfigured = this.botSub.Bot.ConfiguredChannels.indexOf(toChannel.toLowerCase()) >= 0;
        const requireABS = BotUtilsV2.isMigrationOptional(this.botSub.Bot) && allowedChannels.indexOf(toChannel.toLowerCase()) < 0;
        const disabled = FlightUtils.areMigrationRequiredBotsDisabled;
        if (!channelIsConfigured && requireABS && disabled) {
            // show error about non-migrated bot being disabled.
            const mb = new Dialogs.MessageBox({
                okButtonLabel: Resources.okButtonText
            });
            mb.message = Resources.channelRequiresABSBot;
            mb.showDialog();
        } else {
            this.channelConfigNav(toChannel);
        }
    }

    private channelConfigNav(toChannel: string): void {
        this.component.setState({ channel: toChannel, selectedTab: BotTabs.Channels });
        let botType = BotUtils.getBotType(this.component.state.bot);
        let route = Routes(botType)
            .filter(entry => entry.tab === BotTabs.Channels)
            .map(entry => entry.route)
            .shift() || '/bots';
        history.pushState(null, null, `${route}?id=${this.botId}&channelId=${toChannel}`);
    }

    private onChannelConfigLeave() {
        this.component.setState({ channel: "", selectedTab: BotTabs.Channels });
        let botType = BotUtils.getBotType(this.component.state.bot);
        let route = Routes(botType)
            .filter(entry => entry.tab === BotTabs.Channels)
            .map(entry => entry.route)
            .shift() || '/bots';
        history.pushState(null, null, `${route}?id=${this.botId}`);
    }

    private reloadBot() {
        Rest.BotManagerClient.getById(this.botId).then(botSubscription => {
            this.botSub = botSubscription;
            this.component.setState({ bot: botSubscription.Bot });
            this.botEditorController.updateBot(botSubscription.Bot);
        });
    }

    private refreshFromRemote() {
        this.renderPage(this.component.state.bot.Id);
    }

    private setupEventHandlers(): void {
        var $page = $(this.pageContent);
        $page.off();
        $page.on('click', '.regenerate-link', ev => this.onRegenerateKeyClicked(ev));
        $page.on('click', '.perform-test', ev => this.onPerformTest());
        $page.on('click', '.maximize', ev => this.onToggleMaximize(ev));
        $page.on('click', '.refresh-channels', ev => this.refreshFromRemote());
    }

    private pollDeploymentStatus(): JQueryPromise<boolean> {
        let deferred = $.Deferred<boolean>();
        let bot: Rest._BotService = this.component ? this.component.state.bot : null;
        bot = bot || (this.botSub ? this.botSub.Bot : {});
        const isSDKBot = BotUtils.isSDKBot(<Rest.BotService>bot);
        if (bot.Id && !isSDKBot) {
            Rest.BotManagerClient.getProgress(bot.Id, false)
                .then(x => {
                    if (isDeploymentFinished(x)) {
                        if (BotUtils.isABSv1Bot(x)) {
                            // for ABSv1 bot poll deployment.log to make sure the bot is fully deployed
                            return this.kuduClient;
                        }
                        else {
                            this.botPageState.showWebChatPending = false;
                            return deferred.resolve(true);
                        }
                    } else {
                        return deferred.resolve(false);
                    }
                }, (xhr) => {
                    if (xhr && !this.handleTenantSwitching(xhr)) {
                        // we may not have access to the Azure resource
                        // to check bot deployment status, assume it is deployed fine
                        this.botPageState.showWebChatPending = false;
                        return $.Deferred<boolean>().resolve(true);
                    }
                })
                .then((x: any) => {
                    if (_.isBoolean(x)) {
                        return deferred;
                    }
                    if (x instanceof KuduClient) {
                        const client = x;
                        client.get(`${client.scmUri.format()}/api/vfs/site/deployment.log`)
                            .then(log => {
                                this.botPageState.showWebChatPending = false;
                                deferred.resolve(true);
                            })
                            .fail((xhr) => {
                                if (xhr && xhr.status == 404 && client) {
                                    deferred.resolve(false);
                                } else {
                                    deferred.reject(xhr || 'failed to ping bot deployment log');
                                }
                            })
                    }
                })
                .fail(xhr => {
                    this.waitingForDeployment = false;
                    if (xhr && !this.handleTenantSwitching(xhr)) {
                        this.handleHttpResponseError(xhr);
                    }
                });
        } else {
            this.botPageState.showWebChatPending = !isSDKBot;
            deferred.resolve(isSDKBot);
        }
        return deferred;
    }

    private onToggleMaximize(ev: JQueryEventObject) {

        var $page = $(this.pageContent);

        //find the card which is being maximize-toggled
        var $card = $(ev.currentTarget).parent('.grid-card');

        var maxPage = 'maximized';
        var maxCard = 'grid-card-full-width';

        if ($page.hasClass(maxPage)) {
            $card.removeClass(maxCard);
            $page.removeClass(maxPage);
        } else {
            $card.addClass(maxCard);
            $page.addClass(maxPage);
        }
    }

    private onRegenerateKeyClicked(ev: JQueryEventObject): void {
        if (this.component.state.bot && this.component.state.bot.Id) {
            ev.preventDefault();
            var isPrimary = $(ev.target).hasClass('primary');
            Dialogs.MessageBox.ask("Are you sure you want to regenerate this key?").done(() => {
                this.regenerateKey(isPrimary);
            });
        }
    }

    private regenerateKey(isPrimary: boolean) {
        var progressDialog = ProgressDialog.show("Regenerating secret...");
        Rest.BotManagerClient.regenerateSubscriptionKey(this.component.state.bot.Id, isPrimary).done(botSubscription => {
            if (this.isLoaded()) {
                progressDialog.closeDialog();
                this.component.setState({
                    bot: botSubscription.Bot,
                    subscriptionKeys: {
                        primary: botSubscription.PrimarySubscriptionKey
                    }
                });
            }
        }).fail(xhr => {
            progressDialog.closeDialog();
            this.handleHttpResponseError(xhr);
        });
    }

    private onPerformTest(): void {
        var $page = $(this.pageContent);
        var inputText = $page.find(".test-input").val();
        var $output = $page.find(".test-output");
        $output.text('');
        Rest.BotManagerClient.testBot(this.component.state.bot.Id, inputText).done(json => {
            if (!this.isLoaded())
                return;
            try {
                var obj = JSON.parse(json);
                if (obj) {
                    var s = JSON.stringify(obj, null, 4);
                    $output.text(s);
                }
            }
            catch (ex) {
                $output.text(json);
            }
        }).fail(xhr => {
            this.handleHttpResponseError(xhr);
        });
    }

    private onDirtyMessage = (e: JQueryEventObject, dirtyData: any) => {
        if (dirtyData) {
            if (this.component && this.component.state.authoringDirty !== dirtyData.dirty) {
                this.component.setState({ authoringDirty: dirtyData.dirty });
            }
        }
    }

    private onMaskMessage = (e: JQueryEventObject, maskData: any) => {
        if (maskData && maskData.inProcess) {
            this.statusBar = React.createElement(BotProgress, { progress: maskData.context });
            this.showProgressMask = true;
        } else {
            this.statusBar = null;
            this.showProgressMask = false;
        }
    }

    private onErrorMessage = (e: JQueryEventObject, errorData: any) => {
        if (errorData) {
            if (this.component && this.component.state.authoringError !== errorData.hasError) {
                this.component.setState({ authoringError: errorData.hasError });
            }
        }
    }

    public refreshScmType() {
        const codeSetup  = this.botCodeSetupController.component;
        if (codeSetup) {
            codeSetup.refreshScmType();
        }
    }

    public static getConfiguredChannels(bot: Rest.BotService): Rest.Channel[] {

        if (bot.ConfiguredChannels) {
            return clientAppSettings.channels.filter(ch => bot.ConfiguredChannels.indexOf(ch.Id) != -1);
        }
        return [];
    }

    public setAnnouncementState(announcements: IAnnouncements) {
        super.setAnnouncementState(announcements);

        this.botPageState.announcements = announcements;
        if (this.component) {
            this.component.setState({ announcements });
            this.component.forceUpdate();
        }
    }

    protected showEmbedCodes(_lastFocus: React.ReactInstance): void {
        let progressDialog = ProgressDialog.show(Resources.embedPanelGettingCodes);
        Rest.BotManagerClient.getBotContactCard(this.botId).done(contactCard => {
            progressDialog.closeDialog();
            if (!contactCard.Contacts.length) {
                Dialogs.MessageBox.show(Resources.embedPanelNoCodeAvailable);
            }
            else {

                let dialog = new GenericDialog<EmbedCodeView.EmbedCodePanelProps>(EmbedCodeView.EmbedCodePanel, {
                    botId: this.botId,
                    contacts: contactCard.Contacts,
                    webChatClick: () => {
                        dialog.closeDialog();
                        this.onChannelConfigNav("webchat")
                    }
                }, { dialogClass: 'embed-code-dialog' });
                dialog.showDialog(_lastFocus);
            }
        }).fail(xhr => {
            progressDialog.closeDialog();
            this.handleHttpResponseError(xhr);
        });
    }
}



// WEBPACK FOOTER //
// ./Client/v4/Controllers/BotController.ts